import { useEffect, useState, useRef } from 'react';
import { MapContainer as LeafletMap, TileLayer, LayersControl, WMSTileLayer, useMap } from 'react-leaflet';
import { collection, onSnapshot, query, orderBy, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';
import MapSidebar from './MapSidebar';
import LayerCategory, { WMSLayer } from './LayerCategory';
import DrawingTools, { DrawingMode } from './DrawingTools';
import LayerSwipe from './LayerSwipe';
import MapTools from './MapTools';
import AddUserWMSLayer from './AddUserWMSLayer';
import UserLayerCategory from './UserLayerCategory';
import PlaceMarkerByCoords from './PlaceMarkerByCoords';
import DrawingsList from './DrawingsList';
import ColorPickerModal from './ColorPickerModal';
import DroneLayer from './DroneLayer';
import DroneFlightPathsLayer from './DroneFlightPathsLayer';
import ApprovedImagesPanel from './ApprovedImagesPanel';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet/dist/leaflet.css';
import 'leaflet/dist/images/marker-icon.png';
import 'leaflet/dist/images/marker-icon-2x.png';
import 'leaflet/dist/images/marker-shadow.png';
import './MapContainer.css';

// Fix Leaflet default marker icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
  iconUrl: new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href,
  shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
});

const { BaseLayer } = LayersControl;

// Helper function to create colored marker icon with optional title label
const createColoredIcon = (color: string, title?: string) => {
  const svgIcon = `
    <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 9.375 12.5 28.5 12.5 28.5S25 21.875 25 12.5C25 5.596 19.404 0 12.5 0z"
            fill="${color}"
            stroke="#fff"
            stroke-width="2"/>
      <circle cx="12.5" cy="12.5" r="4" fill="#fff"/>
    </svg>
  `;

  const labelHtml = title ? `<div class="marker-label">${title}</div>` : '';

  return L.divIcon({
    html: `${labelHtml}${svgIcon}`,
    className: 'custom-marker-icon',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });
};

interface LayerCategory {
  id: string;
  name: string;
  order: number;
}

interface Drawing {
  id: string;
  type: string;
  geometry: any;
  properties?: any;
  comment?: string;
  title?: string;
  color?: string;
  createdBy: string;
  createdByName: string;
  createdAt: any;
}

// Component to handle drawing controls
const DrawingControlHandler = ({
  drawingMode,
  onDrawingCreated,
  drawings,
  onCommentUpdate,
  onTitleUpdate,
  onColorUpdate,
  onDrawingDelete,
  showSharedDrawings,
  drawingLayerMapRef,
  onOpenColorPicker
}: {
  drawingMode: DrawingMode;
  onDrawingCreated: (layer: L.Layer) => void;
  drawings: Drawing[];
  onCommentUpdate: (drawingId: string, comment: string) => void;
  onTitleUpdate: (drawingId: string, title: string) => void;
  onColorUpdate: (drawingId: string, color: string) => void;
  onDrawingDelete: (drawingId: string) => void;
  showSharedDrawings: boolean;
  drawingLayerMapRef: React.MutableRefObject<Map<string, L.Layer>>;
  onOpenColorPicker: (drawingId: string, currentColor: string) => void;
}) => {
  const map = useMap();
  const featureGroupRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const drawControlRef = useRef<L.Control.Draw | null>(null);

  useEffect(() => {
    const featureGroup = featureGroupRef.current;
    if (!map.hasLayer(featureGroup)) {
      map.addLayer(featureGroup);
    }

    // Initialize draw control
    if (!drawControlRef.current) {
      drawControlRef.current = new L.Control.Draw({
        edit: {
          featureGroup: featureGroup,
        },
        draw: false as any, // We'll enable drawing modes manually
      });
      map.addControl(drawControlRef.current);
    }

    return () => {
      if (drawControlRef.current) {
        map.removeControl(drawControlRef.current);
        drawControlRef.current = null;
      }
      if (map.hasLayer(featureGroup)) {
        map.removeLayer(featureGroup);
      }
    };
  }, [map]);

  // Load existing drawings
  useEffect(() => {
    const featureGroup = featureGroupRef.current;
    const drawingLayerMap = drawingLayerMapRef.current;

    featureGroup.clearLayers();
    drawingLayerMap.clear();

    // Don't show any drawings if toggle is off
    if (!showSharedDrawings) {
      return;
    }

    const handleEdit = (drawingId: string, type: 'title' | 'comment' | 'color', currentValue: string) => {
      if (type === 'color') {
        onOpenColorPicker(drawingId, currentValue || '#3388ff');
      } else if (type === 'title') {
        const newTitle = prompt('Edit title:', currentValue || '');
        if (newTitle !== null && newTitle !== currentValue) {
          onTitleUpdate(drawingId, newTitle);
        }
      } else {
        const newComment = prompt('Add or edit comment:', currentValue || '');
        if (newComment !== null && newComment !== currentValue) {
          onCommentUpdate(drawingId, newComment);
        }
      }
    };

    drawings.forEach((drawing) => {
      try {
        // Parse geometry from string (stored as JSON string in Firestore)
        const geometry = typeof drawing.geometry === 'string'
          ? JSON.parse(drawing.geometry)
          : drawing.geometry;

        let layer: L.Layer | null = null;
        let textLabel: L.Marker | null = null;
        const color = drawing.color || '#3388ff';
        const title = drawing.title || 'Untitled';

        // Special handling for circles
        if (geometry.type === 'Circle') {
          const center: [number, number] = [geometry.coordinates[1], geometry.coordinates[0]];
          layer = L.circle(
            center,
            {
              radius: geometry.radius,
              color: color,
              weight: 4,
            }
          );

          // Add text label at center
          textLabel = L.marker(center, {
            icon: L.divIcon({
              html: `<div class="polygon-label">${title}</div>`,
              className: 'polygon-label-icon',
              iconSize: [0, 0],
              iconAnchor: [0, 0],
            }),
            interactive: false,
          });
        } else if (geometry.type === 'Point') {
          // Create colored marker for Point geometries with title label
          layer = L.marker(
            [geometry.coordinates[1], geometry.coordinates[0]], // lat, lng
            { icon: createColoredIcon(color, title) }
          );
        } else {
          // Load other geometry types as GeoJSON
          const geoJsonLayer = L.geoJSON(geometry, {
            style: { color: color, weight: 4 }
          });
          geoJsonLayer.eachLayer((l) => {
            layer = l;
          });

          // Calculate center for Polygon and LineString
          if (layer && (geometry.type === 'Polygon' || geometry.type === 'LineString')) {
            let center: L.LatLng;
            if ((layer as any).getBounds) {
              const bounds = (layer as any).getBounds();
              center = bounds.getCenter();
            } else if ((layer as any).getLatLng) {
              center = (layer as any).getLatLng();
            } else {
              // Fallback: calculate from first coordinate
              center = L.latLng(geometry.coordinates[0][1], geometry.coordinates[0][0]);
            }

            // Add text label at center
            textLabel = L.marker(center, {
              icon: L.divIcon({
                html: `<div class="polygon-label">${title}</div>`,
                className: 'polygon-label-icon',
                iconSize: [0, 0],
                iconAnchor: [0, 0],
              }),
              interactive: false,
            });
          }
        }

        if (layer) {
          // Create popup content function
          const getPopupContent = () => {
            const title = drawing.title || 'Untitled';
            const comment = drawing.comment || '';
            const createdBy = drawing.createdByName || 'Unknown User';
            const createdDate = drawing.createdAt?.toDate ?
              drawing.createdAt.toDate().toLocaleDateString() :
              'Unknown date';

            return `
              <div style="min-width: 200px;">
                <div style="margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb;">
                  <strong style="color: #2563eb; font-size: 14px;">${title}</strong><br/>
                  <small style="color: #6b7280;">by ${createdBy} • ${createdDate}</small>
                </div>
                <strong>Comment:</strong><br/>
                <div style="margin: 8px 0;">${comment || '<em>No comment yet</em>'}</div>
                <div style="display: flex; gap: 6px; flex-wrap: wrap; margin-top: 10px;">
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('editDrawing', { detail: { id: '${drawing.id}', type: 'title' } }))"
                    style="padding: 6px 10px; cursor: pointer; background: #2563eb; color: white; border: none; border-radius: 4px; font-size: 12px;"
                  >
                    ✏️ Title
                  </button>
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('editDrawing', { detail: { id: '${drawing.id}', type: 'comment' } }))"
                    style="padding: 6px 10px; cursor: pointer; background: #2563eb; color: white; border: none; border-radius: 4px; font-size: 12px;"
                  >
                    💬 Comment
                  </button>
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('editDrawing', { detail: { id: '${drawing.id}', type: 'color' } }))"
                    style="padding: 6px 10px; cursor: pointer; background: #8b5cf6; color: white; border: none; border-radius: 4px; font-size: 12px;"
                  >
                    🎨 Color
                  </button>
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('deleteDrawing', { detail: '${drawing.id}' }))"
                    style="padding: 6px 10px; cursor: pointer; background: #ef4444; color: white; border: none; border-radius: 4px; font-size: 12px;"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            `;
          };

          // Bind popup with initial content
          layer.bindPopup(getPopupContent());

          featureGroup.addLayer(layer);
          drawingLayerMap.set(drawing.id, layer);

          // Add text label if it exists
          if (textLabel) {
            featureGroup.addLayer(textLabel);
          }
        }
      } catch (error) {
        console.error('Error loading drawing:', error);
      }
    });

    // Listen for edit events from popup buttons
    const handleEditEvent = (e: any) => {
      const { id, type } = e.detail;
      const drawing = drawings.find(d => d.id === id);
      if (drawing) {
        const currentValue = type === 'title' ? drawing.title :
                            type === 'color' ? drawing.color :
                            drawing.comment;
        handleEdit(id, type, currentValue || '');
      }
    };

    const handleDeleteEvent = (e: any) => {
      const drawingId = e.detail;
      if (confirm('Are you sure you want to delete this drawing?')) {
        onDrawingDelete(drawingId);
      }
    };

    window.addEventListener('editDrawing', handleEditEvent);
    window.addEventListener('deleteDrawing', handleDeleteEvent);

    return () => {
      window.removeEventListener('editDrawing', handleEditEvent);
      window.removeEventListener('deleteDrawing', handleDeleteEvent);
    };
  }, [drawings, onCommentUpdate, onTitleUpdate, onColorUpdate, onDrawingDelete, showSharedDrawings, drawingLayerMapRef]);

  // Handle drawing mode changes
  useEffect(() => {
    if (drawingMode === 'none') return;

    let drawHandler: L.Draw.Feature | null = null;

    // Configure drawing options with metric units to avoid leaflet-draw bugs
    const drawOptions: any = {
      shapeOptions: {
        color: '#3388ff',
        weight: 4,
      },
      metric: true, // Use metric units
      feet: false, // Don't use feet
      nautic: false, // Don't use nautical miles
    };

    switch (drawingMode) {
      case 'marker':
        drawHandler = new L.Draw.Marker(map as any);
        break;
      case 'polyline':
        drawHandler = new L.Draw.Polyline(map as any, drawOptions);
        break;
      case 'polygon':
        drawHandler = new L.Draw.Polygon(map as any, drawOptions);
        break;
      case 'circle':
        drawHandler = new L.Draw.Circle(map as any, drawOptions);
        break;
    }

    if (drawHandler) {
      drawHandler.enable();
    }

    return () => {
      if (drawHandler) {
        drawHandler.disable();
      }
    };
  }, [drawingMode, map]);

  // Handle drawing events
  useEffect(() => {
    const featureGroup = featureGroupRef.current;

    const handleDrawCreated = (e: any) => {
      const layer = e.layer;
      featureGroup.addLayer(layer);
      onDrawingCreated(layer);
    };

    map.on(L.Draw.Event.CREATED, handleDrawCreated);

    return () => {
      map.off(L.Draw.Event.CREATED, handleDrawCreated);
    };
  }, [map, onDrawingCreated]);

  return null;
};

// Component to capture map reference
const MapRefCapture = ({ mapRef }: { mapRef: React.MutableRefObject<L.Map | null> }) => {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  return null;
};

const MapContainerComponent = () => {
  const { user } = useAuth();
  const [mapReady, setMapReady] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [drawingMode, setDrawingMode] = useState<DrawingMode>('none');
  const [categories, setCategories] = useState<LayerCategory[]>([]);
  const [wmsLayers, setWmsLayers] = useState<WMSLayer[]>([]);
  const [drawings, setDrawings] = useState<Drawing[]>([]);
  const [showSharedDrawings, setShowSharedDrawings] = useState(true);
  const [loading, setLoading] = useState(true);
  const [swipeEnabled, setSwipeEnabled] = useState(false);
  const [swipePosition, setSwipePosition] = useState(50);
  const [leftLayerId, setLeftLayerId] = useState<string>('');
  const [rightLayerId, setRightLayerId] = useState<string>('');
  const [userWmsLayers, setUserWmsLayers] = useState<WMSLayer[]>([]);
  const drawingLayerMapRef = useRef<Map<string, L.Layer>>(new Map());
  const mapRef = useRef<L.Map | null>(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [colorPickerDrawingId, setColorPickerDrawingId] = useState<string>('');
  const [colorPickerCurrentColor, setColorPickerCurrentColor] = useState<string>('#3388ff');
  const [draggedCategoryId, setDraggedCategoryId] = useState<string | null>(null);
  const [dragOverCategoryId, setDragOverCategoryId] = useState<string | null>(null);
  const [showDrones, setShowDrones] = useState(false);
  const [showFlightPaths, setShowFlightPaths] = useState(true);
  const [showApprovedImages, setShowApprovedImages] = useState(true);

  // Fetch categories from Firestore
  useEffect(() => {
    const categoriesQuery = query(
      collection(db, 'layerCategories'),
      orderBy('order', 'asc')
    );

    const unsubscribe = onSnapshot(categoriesQuery, (snapshot) => {
      const fetchedCategories: LayerCategory[] = [];
      snapshot.forEach((doc) => {
        fetchedCategories.push({ id: doc.id, ...doc.data() } as LayerCategory);
      });
      setCategories(fetchedCategories);
    });

    return () => unsubscribe();
  }, []);

  // Fetch WMS layers from Firestore
  useEffect(() => {
    const layersQuery = query(
      collection(db, 'globalWMSLayers'),
      orderBy('order', 'asc')
    );

    const unsubscribe = onSnapshot(layersQuery, (snapshot) => {
      const fetchedLayers: WMSLayer[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        fetchedLayers.push({
          id: doc.id,
          name: data.name,
          url: data.url,
          params: data.params,
          visible: data.defaultVisible || false,
          category: data.category,
          order: data.order || 0,
        });
      });
      console.log('Loaded WMS Layers from Firestore:', fetchedLayers);
      setWmsLayers(fetchedLayers);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch shared drawings from Firestore
  useEffect(() => {
    if (!user) return;

    const drawingsQuery = query(
      collection(db, 'sharedDrawings'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(drawingsQuery, (snapshot) => {
      const fetchedDrawings: Drawing[] = [];
      snapshot.forEach((doc) => {
        fetchedDrawings.push({ id: doc.id, ...doc.data() } as Drawing);
      });
      console.log('Loaded shared drawings from Firestore:', fetchedDrawings);
      setDrawings(fetchedDrawings);
    });

    return () => unsubscribe();
  }, [user]);

  // Fetch user WMS layers from Firestore
  useEffect(() => {
    if (!user) return;

    const userLayersQuery = query(
      collection(db, `users/${user.uid}/wmsLayers`),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(userLayersQuery, (snapshot) => {
      const fetchedUserLayers: WMSLayer[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        fetchedUserLayers.push({
          id: doc.id,
          name: data.name,
          url: data.url,
          params: data.params,
          visible: data.visible !== undefined ? data.visible : true,
          order: 0,
        });
      });
      console.log('Loaded user WMS layers from Firestore:', fetchedUserLayers);
      setUserWmsLayers(fetchedUserLayers);
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    setMapReady(true);
  }, []);

  const handleLayerToggle = (layerId: string, visible: boolean) => {
    setWmsLayers((prev) =>
      prev.map((layer) =>
        layer.id === layerId ? { ...layer, visible } : layer
      )
    );
  };

  const handleUserLayerToggle = async (layerId: string, visible: boolean) => {
    if (!user) return;

    setUserWmsLayers((prev) =>
      prev.map((layer) =>
        layer.id === layerId ? { ...layer, visible } : layer
      )
    );

    try {
      const layerRef = doc(db, `users/${user.uid}/wmsLayers`, layerId);
      await updateDoc(layerRef, { visible });
    } catch (error) {
      console.error('Error updating user layer visibility:', error);
    }
  };

  const handleAddUserWmsLayer = async (layerData: {
    name: string;
    url: string;
    layers: string;
    transparent: boolean;
  }) => {
    if (!user) {
      alert('You must be logged in to add layers');
      return;
    }

    try {
      await addDoc(collection(db, `users/${user.uid}/wmsLayers`), {
        name: layerData.name,
        url: layerData.url,
        params: {
          layers: layerData.layers,
          format: 'image/png',
          transparent: layerData.transparent,
        },
        visible: true,
        createdAt: new Date(),
      });
      console.log('User WMS layer added successfully');
    } catch (error) {
      console.error('Error adding user WMS layer:', error);
      alert('Failed to add WMS layer. Please try again.');
    }
  };

  const handleDeleteUserLayer = async (layerId: string) => {
    if (!user) return;

    if (!confirm('Are you sure you want to delete this layer?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, `users/${user.uid}/wmsLayers`, layerId));
      console.log('User WMS layer deleted');
    } catch (error) {
      console.error('Error deleting user WMS layer:', error);
      alert('Failed to delete layer. Please try again.');
    }
  };

  const handleDrawingModeChange = (mode: DrawingMode) => {
    setDrawingMode(mode);
  };

  const handleDrawingCreated = async (layer: L.Layer) => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    try {
      let geometry: any;
      let properties: any = {};

      // Special handling for circles (not standard GeoJSON)
      if (layer instanceof L.Circle) {
        const circle = layer as L.Circle;
        const center = circle.getLatLng();
        const radius = circle.getRadius();

        // Store as a special circle geometry with center and radius
        geometry = {
          type: 'Circle',
          coordinates: [center.lng, center.lat],
          radius: radius,
        };
        properties.radius = radius;
      } else {
        // Convert other shapes to GeoJSON
        const geoJson = (layer as any).toGeoJSON();
        geometry = geoJson.geometry;
        properties = geoJson.properties || {};
      }

      // Prompt for title and color
      const title = prompt('Enter a title for this drawing (optional):', '') || 'Untitled';
      const color = prompt('Enter a color (hex code, e.g., #ff0000):', '#3388ff') || '#3388ff';

      // Serialize geometry to JSON string to avoid Firestore nested array limitation
      await addDoc(collection(db, 'sharedDrawings'), {
        type: geometry.type,
        geometry: JSON.stringify(geometry), // Store as string
        properties: properties,
        title: title,
        color: color,
        createdBy: user.uid,
        createdByName: user.displayName || user.email || 'Unknown User',
        createdAt: new Date(),
      });

      console.log('Drawing saved to shared drawings');

      // Reset drawing mode after saving
      setDrawingMode('none');
    } catch (error) {
      console.error('Error saving drawing:', error);
      alert('Failed to save drawing. Please try again.');
    }
  };

  const handleClearAllDrawings = async () => {
    if (!user) return;

    const myDrawings = drawings.filter(d => d.createdBy === user.uid);

    if (myDrawings.length === 0) {
      alert('You have no drawings to clear.');
      return;
    }

    if (!confirm(`Are you sure you want to delete all ${myDrawings.length} of your drawing(s)? This cannot be undone.`)) {
      return;
    }

    try {
      // Delete user's drawings from Firestore
      const deletePromises = myDrawings.map((drawing) =>
        deleteDoc(doc(db, 'sharedDrawings', drawing.id))
      );
      await Promise.all(deletePromises);
      console.log('Your drawings cleared');
    } catch (error) {
      console.error('Error clearing drawings:', error);
      alert('Failed to clear drawings. Please try again.');
    }
  };

  const handleCommentUpdate = async (drawingId: string, comment: string) => {
    if (!user) return;

    try {
      const drawingRef = doc(db, 'sharedDrawings', drawingId);
      await updateDoc(drawingRef, {
        comment: comment,
        updatedAt: new Date(),
      });
      console.log('Comment updated');
    } catch (error) {
      console.error('Error updating comment:', error);
      alert('Failed to update comment. Please try again.');
    }
  };

  const handleTitleUpdate = async (drawingId: string, title: string) => {
    if (!user) return;

    try {
      const drawingRef = doc(db, 'sharedDrawings', drawingId);
      await updateDoc(drawingRef, {
        title: title,
        updatedAt: new Date(),
      });
      console.log('Title updated');
    } catch (error) {
      console.error('Error updating title:', error);
      alert('Failed to update title. Please try again.');
    }
  };

  const handleColorUpdate = async (drawingId: string, color: string) => {
    if (!user) return;

    try {
      const drawingRef = doc(db, 'sharedDrawings', drawingId);
      await updateDoc(drawingRef, {
        color: color,
        updatedAt: new Date(),
      });
      console.log('Color updated');
    } catch (error) {
      console.error('Error updating color:', error);
      alert('Failed to update color. Please try again.');
    }
  };

  const handleDrawingDelete = async (drawingId: string) => {
    if (!user) return;

    try {
      await deleteDoc(doc(db, 'sharedDrawings', drawingId));
      console.log('Drawing deleted');
    } catch (error) {
      console.error('Error deleting drawing:', error);
      alert('Failed to delete drawing. Please try again.');
    }
  };

  const handlePlaceMarkerByCoords = async (lat: number, lng: number, title: string, color: string) => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    try {
      // Create a marker geometry
      const geometry = {
        type: 'Point',
        coordinates: [lng, lat], // GeoJSON format: [lng, lat]
      };

      await addDoc(collection(db, 'sharedDrawings'), {
        type: 'Point',
        geometry: JSON.stringify(geometry),
        properties: {},
        title: title,
        color: color,
        createdBy: user.uid,
        createdByName: user.displayName || user.email || 'Unknown User',
        createdAt: new Date(),
      });

      console.log('Marker placed successfully');
    } catch (error) {
      console.error('Error placing marker:', error);
      alert('Failed to place marker. Please try again.');
    }
  };

  const handleZoomToDrawing = (drawingId: string) => {
    const layer = drawingLayerMapRef.current.get(drawingId);
    if (layer && mapRef.current) {
      const bounds = (layer as any).getBounds ? (layer as any).getBounds() : null;
      if (bounds) {
        mapRef.current.fitBounds(bounds, { padding: [50, 50] });
      } else if ((layer as any).getLatLng) {
        const latlng = (layer as any).getLatLng();
        mapRef.current.setView(latlng, 15);
      }
    }
  };

  const handleOpenColorPicker = (drawingId: string, currentColor: string) => {
    setColorPickerDrawingId(drawingId);
    setColorPickerCurrentColor(currentColor);
    setColorPickerOpen(true);
  };

  const handleSaveColor = (newColor: string) => {
    if (colorPickerDrawingId) {
      handleColorUpdate(colorPickerDrawingId, newColor);
    }
  };

  const handleLayerReorder = async (layerId: string, newOrder: number) => {
    try {
      // Update the layer order in Firestore
      const layerRef = doc(db, 'globalWMSLayers', layerId);
      await updateDoc(layerRef, {
        order: newOrder,
        updatedAt: new Date(),
      });

      // Also update other layers' orders to maintain uniqueness
      // Find all layers that need to be reordered
      const affectedLayers = wmsLayers.filter(l => l.id !== layerId && l.order !== undefined && l.order >= newOrder);

      // Update affected layers in Firestore
      const updatePromises = affectedLayers.map(layer => {
        const affectedLayerRef = doc(db, 'globalWMSLayers', layer.id);
        return updateDoc(affectedLayerRef, {
          order: (layer.order || 0) + 1,
        });
      });

      await Promise.all(updatePromises);

      console.log('Layer order updated successfully');
    } catch (error) {
      console.error('Error updating layer order:', error);
      alert('Failed to update layer order. Please try again.');
    }
  };

  const handleCategoryReorder = async (categoryId: string, newOrder: number) => {
    try {
      // Update the category order in Firestore
      const categoryRef = doc(db, 'layerCategories', categoryId);
      await updateDoc(categoryRef, {
        order: newOrder,
        updatedAt: new Date(),
      });

      // Also update other categories' orders to maintain uniqueness
      const affectedCategories = categories.filter(c => c.id !== categoryId && c.order >= newOrder);

      // Update affected categories in Firestore
      const updatePromises = affectedCategories.map(category => {
        const affectedCategoryRef = doc(db, 'layerCategories', category.id);
        return updateDoc(affectedCategoryRef, {
          order: category.order + 1,
        });
      });

      await Promise.all(updatePromises);

      console.log('Category order updated successfully');
    } catch (error) {
      console.error('Error updating category order:', error);
      alert('Failed to update category order. Please try again.');
    }
  };

  // Group layers by category
  const layersByCategory = categories.reduce((acc, category) => {
    acc[category.id] = wmsLayers.filter(
      (layer) => layer.category === category.id
    );
    return acc;
  }, {} as Record<string, WMSLayer[]>);

  // Uncategorized layers
  const uncategorizedLayers = wmsLayers.filter((layer) => !layer.category);

  if (!mapReady) {
    return <div className="map-loading">Loading map...</div>;
  }

  return (
    <div className="map-wrapper">
      <LeafletMap
        center={[69.2652106, 16.0473224]}
        zoom={10}
        style={{ height: '100%', width: '100%' }}
      >
        <LayersControl position="topright">
          <BaseLayer checked name="OpenStreetMap">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </BaseLayer>

          <BaseLayer name="Satellite">
            <TileLayer
              attribution='Imagery &copy; <a href="https://www.esri.com/">Esri</a>'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
          </BaseLayer>

          <BaseLayer name="Terrain">
            <TileLayer
              attribution='Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
            />
          </BaseLayer>
        </LayersControl>

        {/* Render visible WMS layers */}
        {!swipeEnabled && wmsLayers
          .filter((layer) => layer.visible)
          .map((layer) => {
            // Calculate z-index based on order (higher order = on top)
            const zIndex = 1000 + (layer.order || 0);

            console.log('Rendering WMS Layer:', {
              name: layer.name,
              url: layer.url,
              params: layer.params,
              order: layer.order,
              zIndex: zIndex
            });

            return (
              <WMSTileLayer
                key={`${layer.id}-${layer.order}`}
                url={layer.url}
                params={layer.params}
                zIndex={zIndex}
                eventHandlers={{
                  tileerror: (error) => {
                    console.error('WMS Tile Error for', layer.name, ':', error);
                  },
                  tileload: () => {
                    console.log('WMS Tile loaded successfully for', layer.name);
                  }
                }}
              />
            );
          })}

        {/* Render visible user WMS layers */}
        {!swipeEnabled && userWmsLayers
          .filter((layer) => layer.visible)
          .map((layer) => {
            console.log('Rendering User WMS Layer:', {
              name: layer.name,
              url: layer.url,
              params: layer.params
            });

            return (
              <WMSTileLayer
                key={`user-${layer.id}`}
                url={layer.url}
                params={layer.params}
                zIndex={1001}
                eventHandlers={{
                  tileerror: (error) => {
                    console.error('User WMS Tile Error for', layer.name, ':', error);
                  },
                  tileload: () => {
                    console.log('User WMS Tile loaded successfully for', layer.name);
                  }
                }}
              />
            );
          })}

        {/* Render swipe comparison layers */}
        {swipeEnabled && leftLayerId && (
          <WMSTileLayer
            key={`left-${leftLayerId}`}
            url={wmsLayers.find(l => l.id === leftLayerId)?.url || ''}
            params={wmsLayers.find(l => l.id === leftLayerId)?.params || {}}
            pane="swipe-left"
          />
        )}

        {swipeEnabled && rightLayerId && (
          <WMSTileLayer
            key={`right-${rightLayerId}`}
            url={wmsLayers.find(l => l.id === rightLayerId)?.url || ''}
            params={wmsLayers.find(l => l.id === rightLayerId)?.params || {}}
            pane="swipe-right"
          />
        )}

        {/* Drawing Control Handler */}
        {user && (
          <DrawingControlHandler
            drawingMode={swipeEnabled ? 'none' : drawingMode}
            onDrawingCreated={handleDrawingCreated}
            drawings={drawings}
            onCommentUpdate={handleCommentUpdate}
            onTitleUpdate={handleTitleUpdate}
            onColorUpdate={handleColorUpdate}
            onDrawingDelete={handleDrawingDelete}
            showSharedDrawings={showSharedDrawings}
            drawingLayerMapRef={drawingLayerMapRef}
            onOpenColorPicker={handleOpenColorPicker}
          />
        )}

        {/* Map Reference Capture */}
        <MapRefCapture mapRef={mapRef} />

        {/* Layer Swipe Control */}
        <LayerSwipe
          enabled={swipeEnabled}
          sliderPosition={swipePosition}
          onSliderChange={setSwipePosition}
          leftLayerId={leftLayerId}
          rightLayerId={rightLayerId}
        />

        {/* Map Tools (Measure and Print) */}
        <MapTools />

        {/* Drone Flight Paths Layer (rendered below live drones) */}
        <DroneFlightPathsLayer
          wfsUrl="https://geo.droneflow.no/nofo-postgis/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=nofo-postgis%3Adrone_flight_paths&outputFormat=application/json"
          refreshInterval={30000}
          visible={showFlightPaths}
        />

        {/* Drone Telemetry Layer */}
        <DroneLayer
          wfsUrl="https://geo.droneflow.no/nofo-postgis/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=nofo-postgis%3Adrone_telemetry&outputFormat=application/json"
          refreshInterval={5000}
          visible={showDrones}
        />

        {/* Approved Images Panel */}
        <ApprovedImagesPanel visible={showApprovedImages} />
      </LeafletMap>

      <MapSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)}>
        {/* Layer Swipe Comparison */}
        <div className="sidebar-section">
          <div className="sidebar-section-title">Layer Comparison</div>
          <div className="layer-item">
            <input
              type="checkbox"
              id="swipe-toggle"
              className="layer-checkbox"
              checked={swipeEnabled}
              onChange={(e) => {
                setSwipeEnabled(e.target.checked);
                if (!e.target.checked) {
                  setLeftLayerId('');
                  setRightLayerId('');
                }
              }}
            />
            <label htmlFor="swipe-toggle" className="layer-label">
              Enable Swipe Comparison
            </label>
          </div>

          {swipeEnabled && (
            <div style={{ marginTop: '10px', paddingLeft: '8px' }}>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>
                  Left Layer (hidden by slider):
                </label>
                <select
                  value={leftLayerId}
                  onChange={(e) => setLeftLayerId(e.target.value)}
                  style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                >
                  <option value="">Base Map</option>
                  {wmsLayers.map(layer => (
                    <option key={layer.id} value={layer.id}>{layer.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>
                  Right Layer (visible):
                </label>
                <select
                  value={rightLayerId}
                  onChange={(e) => setRightLayerId(e.target.value)}
                  style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                >
                  <option value="">Base Map</option>
                  {wmsLayers.map(layer => (
                    <option key={layer.id} value={layer.id}>{layer.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginTop: '10px', padding: '8px', background: '#f0f9ff', borderRadius: '4px', fontSize: '12px', color: '#1e40af' }}>
                💡 Drag the slider to compare layers side-by-side
              </div>
            </div>
          )}
        </div>

        {/* Drawing Tools Section */}
        {!swipeEnabled && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">Drawing Tools</div>
            <DrawingTools
              activeMode={drawingMode}
              onModeChange={handleDrawingModeChange}
              onClearAll={handleClearAllDrawings}
              drawingCount={drawings.filter(d => d.createdBy === user?.uid).length}
            />

            {/* Place Marker by Coordinates */}
            {user && (
              <PlaceMarkerByCoords onPlace={handlePlaceMarkerByCoords} />
            )}

            {/* My Drawings List */}
            {user && (
              <DrawingsList
                drawings={drawings.filter(d => d.createdBy === user.uid)}
                onZoomTo={handleZoomToDrawing}
                onDelete={handleDrawingDelete}
              />
            )}
          </div>
        )}

        {/* Shared Drawings Toggle */}
        {!swipeEnabled && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">Shared Drawings</div>
            <div className="layer-item">
              <input
                type="checkbox"
                id="shared-drawings-toggle"
                className="layer-checkbox"
                checked={showSharedDrawings}
                onChange={(e) => setShowSharedDrawings(e.target.checked)}
              />
              <label htmlFor="shared-drawings-toggle" className="layer-label">
                Show All User Drawings ({drawings.length})
              </label>
            </div>
          </div>
        )}

        {/* Drone Flight Paths Toggle */}
        <div className="sidebar-section">
          <div className="sidebar-section-title">Drone Flight Paths</div>
          <div className="layer-item">
            <input
              type="checkbox"
              id="flight-paths-toggle"
              className="layer-checkbox"
              checked={showFlightPaths}
              onChange={(e) => setShowFlightPaths(e.target.checked)}
            />
            <label htmlFor="flight-paths-toggle" className="layer-label">
              Show Flight Paths (Updates every 30s)
            </label>
          </div>
        </div>

        {/* Drone Telemetry Toggle */}
        <div className="sidebar-section">
          <div className="sidebar-section-title">Live Drone Tracking</div>
          <div className="layer-item">
            <input
              type="checkbox"
              id="drones-toggle"
              className="layer-checkbox"
              checked={showDrones}
              onChange={(e) => setShowDrones(e.target.checked)}
            />
            <label htmlFor="drones-toggle" className="layer-label">
              Show Live Drones (Updates every 5s)
            </label>
          </div>
        </div>

        {/* Approved Images Toggle */}
        <div className="sidebar-section">
          <div className="sidebar-section-title">Approved Images</div>
          <div className="layer-item">
            <input
              type="checkbox"
              id="approved-images-toggle"
              className="layer-checkbox"
              checked={showApprovedImages}
              onChange={(e) => setShowApprovedImages(e.target.checked)}
            />
            <label htmlFor="approved-images-toggle" className="layer-label">
              Show Approved Images Panel
            </label>
          </div>
        </div>

        {/* WMS Layers Section */}
        <div className="sidebar-section">
          <div className="sidebar-section-title">Map Layers</div>

          {loading ? (
            <div className="empty-message">Loading layers...</div>
          ) : categories.length === 0 && wmsLayers.length === 0 ? (
            <div className="empty-message">
              No layers available yet.
              <br />
              Admin users can add layers from the admin panel.
            </div>
          ) : (
            <>
              {categories.map((category) => {
                const handleCategoryDragStart = (e: React.DragEvent) => {
                  setDraggedCategoryId(category.id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.stopPropagation();
                };

                const handleCategoryDragOver = (e: React.DragEvent) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDragOverCategoryId(category.id);
                  e.stopPropagation();
                };

                const handleCategoryDragLeave = () => {
                  setDragOverCategoryId(null);
                };

                const handleCategoryDrop = (e: React.DragEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverCategoryId(null);

                  if (!draggedCategoryId || draggedCategoryId === category.id) {
                    setDraggedCategoryId(null);
                    return;
                  }

                  handleCategoryReorder(draggedCategoryId, category.order);
                  setDraggedCategoryId(null);
                };

                const handleCategoryDragEnd = () => {
                  setDraggedCategoryId(null);
                  setDragOverCategoryId(null);
                };

                return (
                  <div
                    key={category.id}
                    draggable
                    onDragStart={handleCategoryDragStart}
                    onDragOver={handleCategoryDragOver}
                    onDragLeave={handleCategoryDragLeave}
                    onDrop={handleCategoryDrop}
                    onDragEnd={handleCategoryDragEnd}
                  >
                    <LayerCategory
                      categoryId={category.id}
                      categoryName={category.name}
                      categoryOrder={category.order}
                      layers={layersByCategory[category.id] || []}
                      onLayerToggle={handleLayerToggle}
                      onLayerReorder={handleLayerReorder}
                      onCategoryReorder={handleCategoryReorder}
                      isDraggingCategory={draggedCategoryId === category.id}
                      isDragOverCategory={dragOverCategoryId === category.id}
                    />
                  </div>
                );
              })}

              {uncategorizedLayers.length > 0 && (
                <LayerCategory
                  categoryName="Other Layers"
                  layers={uncategorizedLayers}
                  onLayerToggle={handleLayerToggle}
                  onLayerReorder={handleLayerReorder}
                />
              )}
            </>
          )}
        </div>

        {/* User WMS Layers Section */}
        {user && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">My Personal Layers</div>

            <AddUserWMSLayer onAdd={handleAddUserWmsLayer} />

            {userWmsLayers.length > 0 ? (
              <UserLayerCategory
                layers={userWmsLayers}
                onLayerToggle={handleUserLayerToggle}
                onLayerDelete={handleDeleteUserLayer}
              />
            ) : (
              <div className="empty-message" style={{ marginTop: '10px', fontSize: '12px' }}>
                No personal layers yet. Add one above!
              </div>
            )}
          </div>
        )}
      </MapSidebar>

      {/* Color Picker Modal */}
      <ColorPickerModal
        isOpen={colorPickerOpen}
        currentColor={colorPickerCurrentColor}
        onClose={() => setColorPickerOpen(false)}
        onSave={handleSaveColor}
      />
    </div>
  );
};

export default MapContainerComponent;
